/**
 * BuildScenarioAccordion - Component for rendering individual scenario accordions.
 *
 * ARCHITECTURE:
 * - Receives scenario data and callbacks from BuildScenarioList.
 * - Provides editable headers and user interaction capabilities.
 * - Ensures visual consistency with CreatorAccordion/CreatorDataset forms.
 *
 * RESPONSIBILITIES:
 * - Render scenario details within an accordion.
 * - Allow users to edit scenario headers.
 * - Trigger callbacks for scenario creation, updates, and deletions.
 * - Maintain visual style using Container and PanelContent wrappers.
 */

import React, { forwardRef, useState, useRef, useEffect } from 'react'

import BaseAccordion from '../../../../ui_reusable/BaseAccordion'
import { HeaderLarge500Style } from '../../../../../styles/Text.styles'
import CustomIcons from '../../../../ui_reusable/CustomIcons'
import theme from '../../../../../styles/theme'
import { Container } from '../../../../ui_reusable/styles/CreateInputUtils.styles'
import { mix } from 'polished' // Added import for mix
import { Tooltip, Whisper } from 'rsuite' // Added Whisper and Tooltip imports
import DeleteScenarioModal from './build_scenario_accordion/DeleteScenarioModal' // Added import

import {
  AccordionContainer,
  PanelContainer,
  DynamicHeader,
  IconContainer,
  PanelContent,
  EditableHeaderInput,
  HeaderContainer,
  EditIconContainer,
  // Re-added DeleteIconContainer import
  ButtonKeepersContainer,
} from './styles/BuildScenarioAccordion.styles'

const BuildScenarioAccordion = ({
  children,
  scenarios,
  onAccordionContextReady,
  ...props
}) => {
  return (
    <AccordionContainer>
      <BaseAccordion {...props} contextIdGenerated={onAccordionContextReady}>
        {children}
      </BaseAccordion>
    </AccordionContainer>
  )
}

const Panel = forwardRef(
  (
    {
      header,
      children,
      isButton = false,
      disabled = false, // NEW PROP
      onHeaderChange,
      onHeaderClick, // NEW PROP
      onDeleteClick, // Callback for deleteButton
      disabledText,
      ...props
    },
    ref,
  ) => {
    const [isEditing, setIsEditing] = useState(false)
    const [headerValue, setHeaderValue] = useState(header)
    const inputRef = useRef(null)
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false) // Added state for modal

    useEffect(() => {
      setHeaderValue(header)
    }, [header])

    const handleEditClick = (e) => {
      e.stopPropagation()
      setIsEditing(true)
    }

    const handleInputChange = (e) => {
      setHeaderValue(e.target.value)
    }

    const handleInputBlur = () => {
      setIsEditing(false)
      if (onHeaderChange && headerValue !== header) {
        onHeaderChange(headerValue)
      }
    }

    const handleInputKeyDown = (e) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        inputRef.current?.blur()
      } else if (e.key === 'Escape') {
        setHeaderValue(header) // Reset to original value
        setIsEditing(false)
      }
    }

    useEffect(() => {
      if (isEditing && inputRef.current) {
        inputRef.current.focus()
        inputRef.current.select()
      }
    }, [isEditing])

    const openDeleteModal = () => {
      setIsDeleteModalOpen(true)
    }

    const closeDeleteModal = () => {
      setIsDeleteModalOpen(false)
    }

    const handleDeleteConfirm = () => {
      if (onDeleteClick) {
        onDeleteClick()
      }
      closeDeleteModal()
    }

    // Extract DynamicHeader content into a separate function
    const renderDynamicHeader = ({ isActive, onToggle }) => (
      <DynamicHeader
        isActive={isActive}
        style={{
          opacity: disabled ? 0.5 : 1,
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
        onClick={(e) => {
          if (disabled) {
            e.stopPropagation()
            return
          }

          if (isButton) {
            // If scenario has NOT started
            if (typeof onHeaderClick === 'function') {
              e.stopPropagation()
              onHeaderClick()
            }
            // onToggle() is NOT called if ! isButton is false,
            // effectively preventing the accordion from toggling.
          } else {
            // If scenario HAS started
            onToggle() // Allow toggling for started scenarios
          }
        }}
      >
        {!isButton ? (
          <HeaderContainer>
            {isEditing ? (
              <EditableHeaderInput
                ref={inputRef}
                value={headerValue}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                onKeyDown={handleInputKeyDown}
                onClick={(e) => e.stopPropagation()}
                data-testid={`edit-scenario-name-${headerValue}-input`}
              />
            ) : (
              <>
                <span css={HeaderLarge500Style}>{headerValue}</span>
                <EditIconContainer
                  onClick={handleEditClick}
                  data-testid={`edit-scenario-name-${headerValue}`}
                >
                  <CustomIcons.PenIcon
                    size="xs"
                    color={theme.colors.veryFadedText}
                    hoverColor={mix(
                      0.9,
                      theme.colors.secondary,
                      theme.colors.veryFadedText,
                    )} // Added hoverColor
                  />
                </EditIconContainer>
              </>
            )}
          </HeaderContainer>
        ) : (
          <span css={HeaderLarge500Style}>{headerValue}</span>
        )}
        <ButtonKeepersContainer>
          <IconContainer
            isActive={isActive}
            onClick={(e) => {
              e.stopPropagation()

              if (disabled) {
                return
              }

              if (!isButton) {
                onToggle()
              } else {
                onHeaderClick()
              }
            }}
            style={{}} // Always visible
          >
            {isActive ? (
              <CustomIcons.MinusCircleIcon
                color={theme.colors.standardText}
                hoverColor={mix(
                  0.7,
                  theme.colors.secondary,
                  theme.colors.standardText,
                )} // Use mixed color
              />
            ) : (
              <CustomIcons.PlusCircleIcon
                color={theme.colors.standardText}
                hoverColor={mix(
                  0.7,
                  theme.colors.secondary,
                  theme.colors.standardText,
                )} // Use mixed color
              />
            )}
          </IconContainer>

          <IconContainer
            onClick={(e) => {
              e.stopPropagation()

              if (disabled) {
                return
              }

              if (isActive && !isButton) {
                openDeleteModal() // Open modal for deletion
              }
            }}
            style={
              !isActive || isButton
                ? { visibility: 'hidden', pointerEvents: 'none' }
                : {}
            }
          >
            <CustomIcons.XCircleIcon
              color={theme.colors.error}
              hoverColor={mix(0.7, theme.colors.secondary, theme.colors.error)}
            />
          </IconContainer>
        </ButtonKeepersContainer>
      </DynamicHeader>
    )

    // Extract renderHeader function to handle Whisper wrapping
    const renderHeader = (headerProps) => {
      const dynamicHeader = renderDynamicHeader(headerProps)

      if (disabled) {
        return (
          <Whisper
            placement="top"
            trigger="hover"
            speaker={
              <Tooltip
                style={{
                  backgroundColor: theme.colors.surface,
                  color: theme.colors.veryVeryFadedText,
                  outline: `1px solid ${theme.colors.veryVeryVeryFadedText}`,
                }}
              >
                {disabledText}
              </Tooltip>
            }
          >
            {dynamicHeader}
          </Whisper>
        )
      }

      return dynamicHeader
    }

    return (
      <PanelContainer>
        <BaseAccordion.Panel {...props} ref={ref} renderHeader={renderHeader}>
          <PanelContent>
            <Container>{children}</Container>
          </PanelContent>
        </BaseAccordion.Panel>
        <DeleteScenarioModal
          open={isDeleteModalOpen}
          onClose={closeDeleteModal}
          onConfirm={handleDeleteConfirm}
          scenarioName={headerValue}
        />
      </PanelContainer>
    )
  },
)

BuildScenarioAccordion.Panel = Panel

export default BuildScenarioAccordion

/**
 * Props for BuildScenarioAccordion
 *
 * BuildScenarioAccordion - Component for rendering individual scenario accordions.
 *
 * ARCHITECTURE:
 * - Receives scenario data and callbacks from BuildScenarioList.
 * - Provides editable headers and user interaction capabilities.
 * - Ensures visual consistency with CreatorAccordion/CreatorDataset forms.
 *
 * RESPONSIBILITIES:
 * - Render scenario details within an accordion.
 * - Allow users to edit scenario headers.
 * - Trigger callbacks for scenario creation, updates, and deletions.
 * - Maintain visual style using Container and PanelContent wrappers.
 *
 * Props:
 *
 * Accordion:
 * - Inherits all props from BaseAccordion.
 *
 * Panel:
 *
 * props.header - The current value of the accordion header.
 * props.onHeaderChange - Callback function triggered when the header value changes.
 * props.! isButton - Boolean flag indicating if the scenario has started.
 * props.disabled - Boolean flag indicating if the panel should be disabled (prevents all interactions).
 * props.onHeaderClick - Callback function triggered when the header is clicked.
 * props.children - Content to display inside the accordion.
 *
 * Editable Header:
 * - Allows users to edit the header text.
 * - Displays a pen icon for entering edit mode.
 * - Updates the parent component with the new header value.
 */
